/**
 * First-run onboarding — the pure logic + deterministic write path behind
 * the guided 8-step setup wizard (src/views/onboarding/FirstRunWizard.tsx).
 *
 * Design contract:
 *   - GATING is pure (`shouldShowOnboarding`) so it can be table-tested:
 *     the wizard shows only for a truly cold workspace (zero accounts AND
 *     zero people) that has never completed OR skipped onboarding.
 *   - The completion flag persists exactly like the Brand Brain does
 *     (src/lib/brandBrain/persist.ts): one synthetic org-scoped row in the
 *     existing `brainFacts` collection with a deterministic id — no new
 *     Store methods, no new collection, works on localStorage today and
 *     Firestore the moment the flag flips.
 *   - ACTIVATION is direct founder input (the founder typed every value
 *     into the form), so it MAY write to the store directly — this is the
 *     documented exception to the approval queue, which exists for AGENT
 *     writes. Everything here is deterministic; NO model calls.
 *   - Honest unknowns: empty fields write NOTHING. The Brain renders empty
 *     sections honestly; we never fabricate a fact to fill one.
 */

import { db, nowIso } from './db/store';
import { putBrainFact } from './brain/companyBrain';
import { createPerson } from './db/people';
import { inviteAppUser } from './db/appUsers';
import { createTask } from './db/tasks';
import type { AppUserRole, BrainFact } from './db/types';

// ---------------------------------------------------------------------------
// Draft model — what the wizard collects, step by step
// ---------------------------------------------------------------------------

/** The six selectable workspace modules (step 2). */
export type ModuleKey = 'crm' | 'content' | 'email' | 'cs' | 'dev' | 'proposals';

export interface ModuleMeta {
  key: ModuleKey;
  label: string;
  blurb: string;
}

export const MODULES: ModuleMeta[] = [
  { key: 'crm', label: 'CRM', blurb: 'Accounts, contacts, prospects, pipeline.' },
  { key: 'content', label: 'Content Engine', blurb: 'Idea → draft → published, persona-tuned.' },
  { key: 'email', label: 'Email Marketing', blurb: 'Sequences, sends, and the outbox.' },
  { key: 'cs', label: 'Customer Success', blurb: 'Health, renewals, deliverables owed.' },
  { key: 'dev', label: 'Dev / PM', blurb: 'Tickets, tasks, roadmap, agents.' },
  { key: 'proposals', label: 'Proposals', blurb: 'Proposals, contracts, signatures.' },
];

export interface DraftPersona {
  name: string;
  role: string;
  /** Free-form pains, separated by `;` or newlines. */
  pains: string;
}

export interface DraftInvite {
  email: string;
  role: AppUserRole;
}

export interface DraftGoal {
  goal: string;
  metric: string;
  target: string;
}

export interface OnboardingDraft {
  company: { name: string; website: string; description: string };
  modules: ModuleKey[];
  quality: { good: string; bad: string; guardrails: string };
  personas: DraftPersona[];
  invites: DraftInvite[];
  goals: DraftGoal[];
}

/** A fresh draft with one blank row per repeatable section. */
export function emptyDraft(): OnboardingDraft {
  return {
    company: { name: '', website: '', description: '' },
    modules: [],
    quality: { good: '', bad: '', guardrails: '' },
    personas: [{ name: '', role: '', pains: '' }],
    invites: [{ email: '', role: 'staff' }],
    goals: [{ goal: '', metric: '', target: '' }],
  };
}

/** Canonical step order — drives the pills, progress bar, and validation. */
export const ONBOARDING_STEPS = [
  { key: 'company', label: 'Company' },
  { key: 'modules', label: 'Modules' },
  { key: 'quality', label: 'Quality' },
  { key: 'personas', label: 'Personas' },
  { key: 'team', label: 'Team' },
  { key: 'goals', label: 'Goals' },
  { key: 'review', label: 'Review' },
  { key: 'activate', label: 'Activate' },
] as const;

// ---------------------------------------------------------------------------
// Gating (pure)
// ---------------------------------------------------------------------------

export interface OnboardingCounts {
  accounts: number;
  people: number;
}

/**
 * Show the first-run wizard only for a truly cold workspace: zero accounts,
 * zero people, and no completed/skipped flag. Any real data — or an explicit
 * skip — means we never trap the user in it again.
 */
export function shouldShowOnboarding(
  counts: OnboardingCounts,
  completed: boolean,
): boolean {
  return !completed && counts.accounts === 0 && counts.people === 0;
}

// ---------------------------------------------------------------------------
// Row helpers + per-step validation (pure)
// ---------------------------------------------------------------------------

/** Split a free-form pains string on `;` / newlines into trimmed items. */
export function splitList(raw: string): string[] {
  return raw
    .split(/[;\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(s: string): boolean {
  return EMAIL_RE.test(s.trim());
}

/** Persona rows the founder actually filled in (named). */
export function filledPersonas(draft: OnboardingDraft): DraftPersona[] {
  return draft.personas.filter((p) => p.name.trim());
}

/** Invite rows with an email entered. */
export function filledInvites(draft: OnboardingDraft): DraftInvite[] {
  return draft.invites.filter((i) => i.email.trim());
}

/** Goal rows with a goal entered. */
export function filledGoals(draft: OnboardingDraft): DraftGoal[] {
  return draft.goals.filter((g) => g.goal.trim());
}

/**
 * Validate one step (0-based index into ONBOARDING_STEPS). Returns a
 * human-readable error, or null when the step may advance. Fully blank
 * repeatable rows are ignored everywhere — only half-filled rows block.
 */
export function validateStep(step: number, draft: OnboardingDraft): string | null {
  switch (ONBOARDING_STEPS[step]?.key) {
    case 'company':
      return draft.company.name.trim()
        ? null
        : 'Company name is required to continue.';
    case 'personas': {
      const halfFilled = draft.personas.some(
        (p) => !p.name.trim() && (p.role.trim() || p.pains.trim()),
      );
      return halfFilled ? 'Each persona needs a name.' : null;
    }
    case 'team': {
      const bad = draft.invites.find(
        (i) => i.email.trim() && !isValidEmail(i.email),
      );
      return bad ? `“${bad.email.trim()}” doesn't look like a valid email.` : null;
    }
    case 'goals': {
      const halfFilled = draft.goals.some(
        (g) => !g.goal.trim() && (g.metric.trim() || g.target.trim()),
      );
      return halfFilled ? 'Each goal needs a name.' : null;
    }
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Completion flag — rides brainFacts as one synthetic org-scoped row
// (identical mechanism to BrandBrain persistence; no new collection)
// ---------------------------------------------------------------------------

/** Synthetic account id — onboarding is org-wide, not per-account. */
export const ONBOARDING_ACCOUNT_ID = '__org__';

/** Deterministic doc id so every write upserts the one flag row. */
export const ONBOARDING_FLAG_FACT_ID = `${ONBOARDING_ACCOUNT_ID}:rules:onboardingCompleted`;

/** True once onboarding was completed OR explicitly skipped. */
export async function readOnboardingCompleted(): Promise<boolean> {
  return Boolean(await db.get('brainFacts', ONBOARDING_FLAG_FACT_ID));
}

/** Live subscription to the flag (drives the wizard gate reactively). */
export function subscribeOnboardingCompleted(
  cb: (completed: boolean) => void,
): () => void {
  return db.subscribe('brainFacts', (facts) =>
    cb(facts.some((f) => f.id === ONBOARDING_FLAG_FACT_ID)),
  );
}

/**
 * Set the completion flag. Direct founder input (the founder clicked
 * Activate or Skip), so it writes straight to the store. `value` records
 * HOW onboarding ended — honest provenance even for a settings row.
 */
export async function markOnboardingCompleted(
  via: 'activated' | 'skipped',
): Promise<void> {
  const existing = await db.get('brainFacts', ONBOARDING_FLAG_FACT_ID);
  const fact: BrainFact = {
    id: ONBOARDING_FLAG_FACT_ID,
    accountId: ONBOARDING_ACCOUNT_ID,
    section: 'rules',
    field: 'onboardingCompleted',
    label: 'Onboarding completed',
    value: via,
    source: 'founder',
    confidence: 1,
    version: (existing?.version ?? 0) + 1,
    setBy: 'founder',
    setAt: nowIso(),
  };
  await db.put('brainFacts', fact);
}

// ---------------------------------------------------------------------------
// Review model (pure) — the compiled summary grid for step 7
// ---------------------------------------------------------------------------

export interface ReviewSection {
  title: string;
  items: string[];
}

const NOT_PROVIDED = 'Not provided';

function orNotProvided(items: string[]): string[] {
  return items.length > 0 ? items : [NOT_PROVIDED];
}

/** Compile everything entered into the six review sections, honestly. */
export function reviewSummary(draft: OnboardingDraft): ReviewSection[] {
  const moduleLabels = MODULES.filter((m) => draft.modules.includes(m.key)).map(
    (m) => m.label,
  );
  return [
    {
      title: 'Company',
      items: orNotProvided(
        [
          draft.company.name.trim(),
          draft.company.website.trim(),
          draft.company.description.trim(),
        ].filter(Boolean),
      ),
    },
    { title: 'Modules', items: orNotProvided(moduleLabels) },
    {
      title: 'Quality standards',
      items: orNotProvided(
        [
          draft.quality.good.trim() && `Good: ${draft.quality.good.trim()}`,
          draft.quality.bad.trim() && `Bad: ${draft.quality.bad.trim()}`,
          draft.quality.guardrails.trim() &&
            `Guardrails: ${draft.quality.guardrails.trim()}`,
        ].filter((s): s is string => Boolean(s)),
      ),
    },
    {
      title: 'ICP & personas',
      items: orNotProvided(filledPersonas(draft).map(personaLine)),
    },
    {
      title: 'Team invites',
      items: orNotProvided(
        filledInvites(draft).map((i) => `${i.email.trim()} — ${i.role}`),
      ),
    },
    {
      title: 'Goals & KPIs',
      items: orNotProvided(filledGoals(draft).map(goalLine)),
    },
  ];
}

function personaLine(p: DraftPersona): string {
  const role = p.role.trim() ? ` (${p.role.trim()})` : '';
  const pains = p.pains.trim() ? ` — pains: ${splitList(p.pains).join(', ')}` : '';
  return `${p.name.trim()}${role}${pains}`;
}

function goalLine(g: DraftGoal): string {
  const metric = g.metric.trim() ? ` — ${g.metric.trim()}` : '';
  const target = g.target.trim() ? ` → ${g.target.trim()}` : '';
  return `${g.goal.trim()}${metric}${target}`;
}

// ---------------------------------------------------------------------------
// Activation — the deterministic founder write path (step 8)
// ---------------------------------------------------------------------------

/**
 * Founder-stated facts land at 0.95 on the Brain's 0–1 confidence scale
 * ("confidence 95") — high trust, but not the reserved 1.0 ceiling.
 */
export const FOUNDER_CONFIDENCE = 0.95;

const EVIDENCE = 'First-run onboarding wizard';
const SET_BY = 'founder';

export interface ActivationResult {
  factsWritten: number;
  personasCreated: number;
  invitesQueued: number;
  taskCreated: boolean;
}

/**
 * Perform every activation write. Direct founder input → direct store
 * writes (the approval queue governs AGENT writes; nothing here is an
 * agent). Deterministic, no model calls; empty fields write nothing.
 */
export async function activateOnboarding(
  draft: OnboardingDraft,
): Promise<ActivationResult> {
  let factsWritten = 0;

  const writeFact = async (
    section: 'identity' | 'icp' | 'goals' | 'rules',
    field: string,
    label: string,
    value: string,
  ) => {
    if (!value.trim()) return; // honest unknown — never write an empty fact
    const res = await putBrainFact({
      accountId: ONBOARDING_ACCOUNT_ID,
      section,
      field,
      label,
      value: value.trim(),
      source: 'founder',
      confidence: FOUNDER_CONFIDENCE,
      evidence: EVIDENCE,
      setBy: SET_BY,
    });
    if (res.written) factsWritten++;
  };

  // Identity
  await writeFact('identity', 'companyName', 'Company name', draft.company.name);
  await writeFact('identity', 'website', 'Website', draft.company.website);
  await writeFact(
    'identity',
    'description',
    'What the company does',
    draft.company.description,
  );
  await writeFact(
    'identity',
    'activeModules',
    'Active modules',
    MODULES.filter((m) => draft.modules.includes(m.key))
      .map((m) => m.label)
      .join(', '),
  );

  // ICP / personas summary
  const personas = filledPersonas(draft);
  await writeFact(
    'icp',
    'personas',
    'ICP personas',
    personas.map(personaLine).join('; '),
  );

  // Goals
  await writeFact(
    'goals',
    'goals',
    'Goals & KPIs',
    filledGoals(draft).map(goalLine).join('; '),
  );

  // Quality standards / guardrails
  await writeFact('rules', 'definitionOfGood', 'Definition of good', draft.quality.good);
  await writeFact('rules', 'definitionOfBad', 'Definition of bad', draft.quality.bad);
  await writeFact('rules', 'guardrails', 'Hard guardrails', draft.quality.guardrails);

  // Personas → real records in the SAME people collection the tab reads.
  let personasCreated = 0;
  for (const p of personas) {
    const pains = splitList(p.pains);
    await createPerson({
      category: 'persona',
      name: p.name.trim(),
      title: p.role.trim() || undefined,
      archetype: p.role.trim() || p.name.trim(),
      summary: pains.length > 0 ? `Pain points: ${pains.join(', ')}` : undefined,
      profile: {
        role: p.role.trim() || undefined,
        painPoints: pains.length > 0 ? pains : undefined,
      },
      tags: ['icp', 'onboarding'],
    });
    personasCreated++;
  }

  // Team invites → the pending appUsers records the admin flow + first-
  // sign-in provisioning already understand. Duplicates are skipped, never
  // merged (inviteAppUser throws; an existing record wins).
  let invitesQueued = 0;
  for (const invite of filledInvites(draft)) {
    if (!isValidEmail(invite.email)) continue;
    try {
      await inviteAppUser({ email: invite.email.trim(), role: invite.role });
      invitesQueued++;
    } catch {
      // Already exists — leave the existing record untouched.
    }
  }

  // Seed the one first task.
  await createTask({
    title: 'Explore your dashboard — Argo highlights what needs attention',
    notes:
      'Seeded by first-run onboarding. The dashboard surfaces overdue follow-ups, ' +
      'billing events, and drifting work as they appear.',
    createdBy: 'onboarding',
  });

  await markOnboardingCompleted('activated');

  return { factsWritten, personasCreated, invitesQueued, taskCreated: true };
}
